/* global google */ // `google` is injected globally by the GSI script tag in index.html
import React, { useEffect, useState } from "react";
import { useGoogleLogin, googleLogout } from "@react-oauth/google";
import axios from "axios";
import { Button, message, Popconfirm } from "antd";
import { IoLogOutOutline, IoShieldCheckmarkOutline } from "react-icons/io5";
import { FcGoogle } from "react-icons/fc";
import { CALENDAR_SCOPE } from "../../../utils/googleCalendar";

const USER_STORAGE_KEY = "user";
const UMT_EMAIL_DOMAIN = "@umt.edu.pk";
const STUDENT_EMAIL_PATTERN = /^[a-z]\d+@umt\.edu\.pk$/i;

/** Reads and validates the cached session, clearing it if it's malformed or expired. */
const readStoredUser = () => {
  let raw;
  try {
    raw = localStorage.getItem(USER_STORAGE_KEY);
  } catch {
    // localStorage can throw in private/locked-down browsing contexts.
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    // Google's decoded credential carries `exp` (seconds since epoch); honor it
    // instead of trusting a cached session forever.
    if (parsed?.exp && parsed.exp * 1000 < Date.now()) {
      localStorage.removeItem(USER_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    localStorage.removeItem(USER_STORAGE_KEY);
    return null;
  }
};

const deriveUserType = (email) =>
  email && STUDENT_EMAIL_PATTERN.test(email) ? "student" : "facultyOrStaff";

const getInitials = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const AuthButtons = ({ setUserType, setIsLoggedIn, onLogout, onUserChange, onCalendarToken }) => {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const storedUser = readStoredUser();
    if (!storedUser) return;

    setUser(storedUser);
    setIsLoggedIn(true);
    setUserType(deriveUserType(storedUser.email));
    onUserChange?.(storedUser);
    // Only ever needs to run once, on mount — the setters/callback identity
    // changing on later renders shouldn't re-trigger this restore.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A single Google popup both signs the user in and grants Calendar access,
  // so "add to calendar" needs no second consent step afterwards.
  const handleLoginSuccess = async (tokenResponse) => {
    const accessToken = tokenResponse?.access_token;
    if (!accessToken) {
      message.error("Google didn't return a valid sign-in. Please try again.");
      return;
    }

    let profile;
    try {
      const { data } = await axios.get("https://www.googleapis.com/oauth2/v3/userinfo", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      profile = data;
    } catch {
      message.error("We couldn't read your Google profile. Please try again.");
      return;
    }

    const email = profile.email?.toLowerCase().trim();

    if (!email || !email.endsWith(UMT_EMAIL_DOMAIN)) {
      try {
        localStorage.removeItem(USER_STORAGE_KEY);
      } catch {
        // ignore storage access errors
      }
      if (typeof google !== "undefined" && google.accounts?.oauth2) {
        google.accounts.oauth2.revoke(accessToken, () => {});
      }
      message.error("Only official UMT (@umt.edu.pk) accounts are allowed to sign in.");
      return;
    }

    const expiresInSeconds = tokenResponse.expires_in || 3600;
    const sessionUser = {
      email,
      name: profile.name,
      picture: profile.picture,
      // Same 1-hour session lifetime the old ID-token sign-in had.
      exp: Math.floor(Date.now() / 1000) + expiresInSeconds,
    };

    try {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(sessionUser));
    } catch {
      // Non-fatal: session just won't persist across a refresh.
    }
    setUser(sessionUser);
    setIsLoggedIn(true);
    setUserType(deriveUserType(email));
    onUserChange?.(sessionUser);
    onCalendarToken?.({
      token: accessToken,
      expiresAt: Date.now() + (expiresInSeconds - 60) * 1000,
    });
    message.success(`Welcome, ${sessionUser.name || "there"}!`);
  };

  const login = useGoogleLogin({
    scope: `openid email profile ${CALENDAR_SCOPE}`,
    onSuccess: handleLoginSuccess,
    onError: () => message.error("Google sign-in failed. Please try again."),
  });

  const handleLogout = () => {
    googleLogout();
    try {
      localStorage.removeItem(USER_STORAGE_KEY);
    } catch {
      // ignore storage access errors
    }
    setUser(null);
    setIsLoggedIn(false);
    setUserType(null);
    onUserChange?.(null);
    onLogout?.();
    message.info("You've been signed out.");
  };

  if (user) {
    return (
      <div className="auth-card auth-card--signed-in">
        <div className="auth-signed-in-row">
          <span className="welcome-avatar">{getInitials(user.name) || "?"}</span>
          <div className="welcome-message">
            <strong>{user.name || "Welcome back"}</strong>
            <small>{user.email}</small>
          </div>
          <Popconfirm
            title="Sign out of Campus Pulse?"
            okText="Sign out"
            cancelText="Cancel"
            placement="bottomRight"
            onConfirm={handleLogout}
          >
            <Button
              type="text"
              icon={<IoLogOutOutline style={{ verticalAlign: "-2px" }} />}
              className="logout-btn"
            >
              Logout
            </Button>
          </Popconfirm>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-card">
      <div className="auth-card-icon">
        <IoShieldCheckmarkOutline />
      </div>
      <div className="auth-card-copy">
        <p className="auth-card-title">Sign in to unlock more Campus Pulse content</p>
        <p className="auth-card-hint">
          Only official <strong>@umt.edu.pk</strong> Google accounts can sign in.
        </p>
      </div>
      <div className="google-login-wrap">
        <button type="button" className="google-signin-btn" onClick={() => login()}>
          <FcGoogle /> Sign in with Google
        </button>
      </div>
    </div>
  );
};

export default AuthButtons;
